import { formatInTimeZone } from "date-fns-tz";

const NYC_TIMEZONE = "America/New_York";

/**
 * Options to customize the calendar file generation.
 */
interface CalendarOptions {
  /** UTM campaign parameter (default: "aiml4pi_annual_meeting") */
  readonly utm_campaign?: string;
  /** UTM content parameter (default: "annual_meeting_calendar_download") */
  readonly utm_content?: string;
  /** Filename for the downloaded .ics file (default: "aiml4pi-calendar-event.ics") */
  readonly filename?: string;
  /** PRODID for the calendar file (default: "-//Artificial Intelligence (AI) and Primary Immunodeficiency (PI)//Event Reminder//EN") */
  readonly prodId?: string;
  /** Domain name to use in the UID (default: value from NEXT_PUBLIC_DOMAIN_NAME or "aiml4pi.org") */
  readonly domainName?: string;
}

/**
 * Parameters for the calendar event generation.
 */
interface CalendarEventParams {
  /** The name of the event */
  readonly eventName: string;
  /** A description of the event */
  readonly eventDescription: string;
  /** The event start date (YYYY-MM-DD format) */
  readonly startDate: string;
  /** The event start time (HH:mm:ss format, NYC-localized) */
  readonly startTime: string;
  /** The event end date (YYYY-MM-DD format) - optional, defaults to startDate if not provided */
  readonly endDate?: string;
  /** The event end time (HH:mm:ss format, NYC-localized) - optional */
  readonly endTime?: string;
  /** Physical location of the event */
  readonly location?: string;
  /** Optional parameters to customize UTM params, filename, PRODID, and domain name */
  readonly options?: CalendarOptions;
}

/**
 * Sanitizes a string for use in iCalendar format by escaping special characters.
 *
 * @param text - The text to sanitize
 * @returns Sanitized text safe for iCalendar format
 */
const sanitizeICSText = (text: string): string => {
  if (!text) return "";

  // Replace newlines, commas, semicolons, and backslashes according to iCalendar spec
  return text
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\n/g, "\\n");
};

/**
 * Formats a date and time for iCalendar format.
 *
 * @param date - The date in YYYY-MM-DD format
 * @param time - The time in HH:mm:ss format
 * @returns Formatted date-time string or empty string if error
 */
const formatDateTime = (date: string, time: string): string => {
  try {
    const dateTimeString = `${date}T${time}`;
    const dateTime = new Date(dateTimeString);

    if (isNaN(dateTime.getTime())) {
      throw new Error(`Invalid date/time: ${dateTimeString}`);
    }

    return formatInTimeZone(dateTime, NYC_TIMEZONE, "yyyyMMdd'T'HHmmss");
  } catch (error) {
    // eslint-disable-next-line no-console -- error boundary for date parsing
    console.error("Error formatting date/time:", error);
    return "";
  }
};

/**
 * Generates and downloads a calendar (.ics) file for an event using named parameters.
 *
 * @param params - The event parameters including name, description, dates, times, and options
 * @returns The ICS content string or undefined on error
 */
// eslint-disable-next-line max-lines-per-function, complexity -- ICS file generation with date formatting, content building, and browser download
export const generateCalendarFile = (params: CalendarEventParams): string | undefined => {
  try {
    const {
      eventName,
      eventDescription,
      startDate,
      startTime,
      endDate,
      endTime,
      location,
      options,
    } = params;

    // Use the optional values or defaults if not provided
    const utmCampaign = options?.utm_campaign ?? "aiml4pi_annual_meeting";
    const utmContent = options?.utm_content ?? "annual_meeting_calendar_download";
    const filename = options?.filename ?? "aiml4pi-calendar-event.ics";
    const prodId =
      options?.prodId ??
      "-//Artificial Intelligence (AI) and Primary Immunodeficiency (PI)//Event Reminder//EN";
    const domainName = options?.domainName ?? process.env.NEXT_PUBLIC_DOMAIN_NAME ?? "aiml4pi.org";

    // Format the start date/time
    const formattedStart = formatDateTime(startDate, startTime);
    if (!formattedStart) {
      throw new Error("Failed to format start date and time.");
    }

    // Format the end date/time if provided
    // eslint-disable-next-line functional/no-let -- conditionally assigned based on endTime presence
    let formattedEnd = "";
    if (endTime) {
      formattedEnd = formatDateTime(endDate || startDate, endTime);
      if (!formattedEnd) {
        throw new Error("Failed to format end date and time.");
      }
    }

    // Sanitize inputs for the iCalendar format
    const sanitizedName = sanitizeICSText(eventName);
    const sanitizedDesc = sanitizeICSText(eventDescription);

    // Build dynamic UTM parameters for tracking
    const utmParams = new URLSearchParams({
      utm_campaign: utmCampaign,
      utm_content: utmContent,
      utm_medium: "calendar",
      utm_source: "calendar_event",
    }).toString();

    // Construct the URL where users can access the event (include UTM tracking)
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL ?? "https://aiml4pi.org";
    const calendarUrl = `${siteUrl}/meeting/agenda-2025?${utmParams}`;

    const sanitizedLocation = location ? sanitizeICSText(location) : calendarUrl;

    // Create a unique ID for the event
    const uniqueId = `${new Date().getTime()}@${domainName}`;

    // Build the ICS file content
    // eslint-disable-next-line functional/no-let -- string built incrementally with conditional DTEND
    let icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:${prodId}
BEGIN:VEVENT
UID:${uniqueId}
DTSTAMP:${formattedStart}Z
DTSTART;TZID=${NYC_TIMEZONE}:${formattedStart}`;

    // Add end time if provided
    if (formattedEnd) {
      icsContent += `\nDTEND;TZID=${NYC_TIMEZONE}:${formattedEnd}`;
    }

    icsContent += `\nSUMMARY:${sanitizedName}
DESCRIPTION:${sanitizedDesc}
LOCATION:${sanitizedLocation}
URL:${calendarUrl}
END:VEVENT
END:VCALENDAR`;

    // Client-side only code - make sure this only runs in the browser
    if (typeof window !== "undefined") {
      // Create a Blob from the ICS content and generate a downloadable URL
      const blob = new Blob([icsContent], { type: "text/calendar" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      // eslint-disable-next-line functional/immutable-data -- DOM anchor API for file download
      link.href = url;
      // eslint-disable-next-line functional/immutable-data -- DOM anchor API for file download
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the Blob URL to avoid memory leaks
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 100);
    } else {
      // eslint-disable-next-line no-console -- informational message for SSR context
      console.log("Calendar generation is only available in browser environments");
    }

    return icsContent;
  } catch (error) {
    // eslint-disable-next-line no-console -- error boundary for calendar generation
    console.error("Error generating calendar file:", error);
    return undefined;
  }
};
